<template>
  <v-container fluid class="py-0 px-2">
    <v-text-field
      v-model="search"
      background-color="colorSearchSettings"
      clearable
      :append-icon="mdiMagnify"
      :label="$t('label.search')"
      hide-details
    ></v-text-field>
    <div style="min-height: 1px"></div>
    <v-data-table
      :headers="headers"
      :items="tableData"
      item-key="id"
      :height="$store.state.windowSize.y - 150"
      :search="search"
      fixed-header
      must-sort
      disable-pagination
      hide-default-footer
      class="tableCursor"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>{{ item.type }}</td>
          <td>{{ item.name }}</td>
          <td>
            {{ item.category === '' ? $t('label.public') : '' }}
            {{ item.category === 'PRC' ? $t('label.private') : '' }}
            {{ item.category === 'VIC' ? $t('label.visitorcard') : '' }}
          </td>
          <td
            v-if="$store.state.auth.userSettings.accDocumentTypes.update"
            class="px-2 tableSelectCursor"
            @click="((modalEdit = true), (documentType = { ...item }))"
          >
            <v-icon>{{ mdiPen }}</v-icon>
          </td>
          <td v-else></td>
        </tr>
      </template>
    </v-data-table>
    <v-row>
      <v-col cols="3">
        <v-btn v-if="$store.state.auth.userSettings.accDocumentTypes.write" block color="colorNew" @click="dataNew">
          {{ $t('label.addNew') }}
        </v-btn>
      </v-col>
    </v-row>
    <!----------DIALOGS------------------------------>
    <v-dialog v-model="modalEdit" persistent width="70%">
      <v-card>
        <v-card-title class="headline colorHeadlineStandard" primary-title>
          {{ $tc('label.documentType') }}
          <v-spacer />
          <v-icon large @click="modalEdit = false">{{ mdiClose }}</v-icon>
        </v-card-title>
        <v-card-text class="pa-3">
          <v-form v-model="validForm" ref="documentTypeForm">
            <v-row>
              <v-col>
                <v-tooltip bottom :open-delay="1000">
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :label="$tc('label.documentType')"
                      maxlength="2"
                      :rules="[ruleRequired]"
                      :disabled="documentType.id != 0"
                      v-model="documentType.type"
                      hide-details
                      v-on="on"
                      autofocus
                    />
                  </template>
                  <span v-html="$t('tt.documentType')" />
                </v-tooltip>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-tooltip bottom :open-delay="1000">
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :label="$t('label.documentName')"
                      maxlength="64"
                      :rules="[ruleRequired]"
                      v-model="documentType.name"
                      hide-details
                      v-on="on"
                    />
                  </template>
                  <span v-html="$t('tt.documentName')" />
                </v-tooltip>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-tooltip bottom :open-delay="1000">
                  <template v-slot:activator="{ on }">
                    <div v-on="on">
                      <v-select
                        v-model="documentType.category"
                        :label="$t('label.category')"
                        :items="listCategories"
                        item-text="text"
                        item-value="value"
                      />
                    </div>
                  </template>
                  <span v-html="$t('tt.documentCategory')" />
                </v-tooltip>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-btn
            v-if="documentType.id && $store.state.auth.userSettings.accDocumentTypes.delete"
            color="colorDelete"
            @click="modalDelete = true"
          >
            {{ $tc('btn.delete') }}
          </v-btn>
          <v-spacer />
          <v-btn color="colorSave" @click="dataSave">{{ $tc('btn.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <DialogConfirmDelete
      :header="$t('label.delete')"
      :body="`${$t('msg.confirmDelete')}: ${documentType.name}?`"
      :openDialog.sync="modalDelete"
      v-on:confirm="dataDelete"
    />
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue';
import axios from 'axios';
import DialogConfirmDelete from '@/components/DialogConfirmDelete.vue';

import { mdiMagnify, mdiPen, mdiClose } from '@mdi/js';

export default Vue.extend({
  name: 'SettingsDocumentTypes',
  components: { DialogConfirmDelete },
  data: () => ({
    modalEdit: false,
    modalDelete: false,
    validForm: false,
    tableData: [],
    search: '',
    documentType: {
      id: 0,
      name: '',
      type: '',
      category: '',
    },
    mdiMagnify: mdiMagnify,
    mdiPen: mdiPen,
    mdiClose: mdiClose,
  }),
  computed: {
    ruleRequired() {
      return (v: any) => !!v || this.$i18n.t('msg.fieldIsMandatory');
    },
    headers: function () {
      return [
        { text: this.$i18n.t('label.type'), value: 'type' },
        { text: this.$i18n.t('label.itemName'), value: 'name' },
        { text: this.$i18n.t('label.category'), value: 'category' },
        { sortable: false, width: '48px' },
      ];
    },
    listCategories: function () {
      return [
        { text: this.$i18n.tc('label.public'), value: '' },
        { text: this.$i18n.tc('label.private'), value: 'PRC' },
        { text: this.$i18n.tc('label.visitorcard'), value: 'VIC' },
      ];
    },
  },
  mounted() {
    this.dataLoad();
  },
  methods: {
    dataLoad() {
      axios
        .get('/api/v1/documenttype')
        .then((response) => {
          this.tableData = response.data;
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
    },
    dataNew() {
      const defaults: any = this.$options;
      this.documentType = { ...defaults.data().documentType };
      this.modalEdit = true;
      this.$nextTick(function () {
        const form: any = this.$refs.documentTypeForm;
        form.validate();
      });
    },
    dataSave() {
      const form: any = this.$refs.documentTypeForm;
      if (form.validate() !== true) {
        this.$store.commit('globalMessage', this.$i18n.t('msg.formNotValid'));
      } else {
        if (this.documentType.id === 0) {
          axios
            .post('/api/v1/documenttype', this.documentType)
            .then(() => {
              this.dataLoad();
              this.modalEdit = false;
            })
            .catch((err) => {
              this.$store.dispatch('catchErrHandler', err);
            });
        } else {
          axios
            .put('/api/v1/documenttype', this.documentType)
            .then(() => {
              this.dataLoad();
              this.modalEdit = false;
            })
            .catch((err) => {
              this.$store.dispatch('catchErrHandler', err);
            });
        }
      }
    },
    dataDelete() {
      axios
        .delete(`/api/v1/documenttype/${this.documentType.id}`)
        .then(() => {
          this.dataLoad();
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
      this.modalDelete = false;
      this.modalEdit = false;
    },
  },
});
</script>
